import { getDefaultRequestOptions } from 'owa-headers';
import { isFeatureEnabled } from 'owa-feature-flags';
import { calendarEvent } from 'owa-calendar-event-converter';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ClientItemId } from 'owa-client-ids';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import EventScope from 'owa-service/lib/contract/EventScope';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import ItemResponseShape from 'owa-service/lib/contract/ItemResponseShape';
import getCalendarEventRequest from 'owa-service/lib/factory/getCalendarEventRequest';
import itemId from 'owa-service/lib/factory/itemId';
import recurringMasterItemId from 'owa-service/lib/factory/recurringMasterItemId';
import getCalendarEventOperation from 'owa-service/lib/operation/getCalendarEventOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { lazyLogServiceCallForDiagnostics } from 'owa-calendar-forms-diagnostics';
import ItemId from 'owa-service/lib/contract/ItemId';
import GetCalendarEventJsonResponse from 'owa-service/lib/contract/GetCalendarEventJsonResponse';

/**
 * Makes the GetCalendarEvent call to the server to get the calendar event object
 * @param id id of the calendar event that needs to be fetched
 * @param shape shape of the calendar event object that will be returned
 * @param eventScope flag which says whether we want series master or series instance
 */
export async function getCalendarEventService(
    calendarEventId: ClientItemId,
    shape: ItemResponseShape,
    eventScope: EventScope = EventScope.Default,
    seriesMasterId: ItemId = undefined
): Promise<CalendarEvent> {
    const { Id: id, mailboxInfo } = calendarEventId;
    const fetchMasterItem =
        eventScope == EventScope.AllInstancesInSeries ||
        eventScope == EventScope.ThisAndFollowingInstances;

    let responseMessage,
        response = null,
        result = null;

    const requestOptions = getDefaultRequestOptions(mailboxInfo) || {};
    requestOptions.datapoint = {
        customData: {
            itemId: id,
            fetchingMasterItem: fetchMasterItem,
        },
        jsonCustomData: (json: GetCalendarEventJsonResponse) => {
            let message = json.Body.ResponseMessages.Items[0] as ItemInfoResponseMessage;
            return {
                responseClass: message.ResponseClass,
                responseCode: message.ResponseCode,
            };
        },
    };
    try {
        response = await getCalendarEventOperation(
            {
                Header: getJsonRequestHeader(),
                Body: getCalendarEventRequest({
                    EventIds: [
                        fetchMasterItem
                            ? seriesMasterId
                                ? itemId({ Id: seriesMasterId.Id })
                                : recurringMasterItemId({ OccurrenceId: id })
                            : itemId({ Id: id }),
                    ],
                    ItemShape: shape,
                    IncludeDeclinedMeetings: isFeatureEnabled('cal-surface-declinedMeetings'),
                }),
            },
            requestOptions
        );
        let responseMessage = response.Body.ResponseMessages.Items[0] as ItemInfoResponseMessage;
        if (responseMessage != null && responseMessage.ResponseClass == 'Success') {
            let calendarItem = responseMessage.Items[0] as CalendarItem;
            if (calendarItem != null) {
                result = calendarEvent(calendarItem, calendarEventId.mailboxInfo);
            }
        }
    } finally {
        // Log the service call for forms diagnostics panel, regardless of whether the request succeeded or threw exception
        if (isFeatureEnabled('cal-mf-diagnostics')) {
            lazyLogServiceCallForDiagnostics.import().then(logServiceCall =>
                logServiceCall({
                    action: 'GetCalendarEvent',
                    status: responseMessage ? responseMessage.ResponseCode : 'Error',
                    response: response && response.Body,
                })
            );
        }
    }

    // In case of error, we are ignoring it
    return result;
}
